import { Link } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { ChevronDown, ExternalLink, type LucideIcon } from 'lucide-react';

import { Button } from '~shared/shadcn/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '~shared/shadcn/ui/dropdown-menu';
import { cn } from '~shared/shadcn/lib/utils';

import { isActiveSection } from './utils';

export interface NavItem {
  id?: string;
  to?: string; // internal link
  href?: string; // external link
  label?: string;
  Icon: LucideIcon;
  description?: string;
  onClick?: () => void;
  activePaths?: Array<string>;
}

export interface NavGroup {
  id: string;
  label?: string;
  column?: 1 | 2;
  items: Array<NavItem>;
}

interface NavSectionProps {
  label: string;
  sectionKey: string;
  sections: Array<NavGroup>;
  pathname: string;
  activeMenu: string | null;
  setActiveMenu: (menu: string | null) => void;
  columns?: 1 | 2;
  widthClassName?: string;
  align?: 'start' | 'end';
}

const itemClassName = cn(
  'group/item flex min-h-[42px] cursor-pointer items-center gap-2.5 rounded-lg px-2 py-1.5',
  'text-[13px] leading-5 text-slate-700 outline-none transition-colors',
  'focus:bg-slate-100 focus:text-slate-950 data-[highlighted]:bg-slate-100 data-[highlighted]:text-slate-950',
  '[&_svg]:pointer-events-none [&_svg]:shrink-0',
);

const iconClassName = cn(
  'flex size-7 shrink-0 items-center justify-center rounded-md border border-transparent bg-slate-100',
  'text-slate-500 transition-colors group-data-[highlighted]/item:bg-[#fff4bf] group-data-[highlighted]/item:text-slate-800',
);

const isPathMatch = (pathname: string, path?: string) => {
  if (!path) return false;
  return pathname === path || pathname.startsWith(`${path}/`);
};

const isItemActive = (pathname: string, item: NavItem) =>
  isPathMatch(pathname, item.to) ||
  (item.activePaths?.some((path) => isPathMatch(pathname, path)) ?? false);

const NavItemContent = ({
  item,
  isActive,
}: {
  item: NavItem;
  isActive: boolean;
}) => (
  <>
    <span
      className={cn(
        iconClassName,
        isActive && 'border-[#ffd60a]/50 bg-[#fff4bf] text-slate-900',
      )}
    >
      <item.Icon className='size-4' strokeWidth={2} aria-hidden />
    </span>
    <span className='flex min-w-0 flex-1 flex-col'>
      <span className='flex items-center gap-1.5 font-medium tracking-[-0.01em]'>
        <span className='truncate'>{item.label}</span>
        {item.href ? (
          <ExternalLink className='size-3 text-slate-400' aria-hidden />
        ) : null}
      </span>
      {item.description ? (
        <span className='truncate text-[11px] font-normal leading-4 text-slate-500'>
          {item.description}
        </span>
      ) : null}
    </span>
    {isActive ? (
      <span className='size-1.5 shrink-0 rounded-full bg-[#ffc300]' />
    ) : null}
  </>
);

const NavDropdownItem = ({
  item,
  pathname,
}: {
  item: NavItem;
  pathname: string;
}) => {
  const active = isItemActive(pathname, item);
  const className = cn(itemClassName, active && 'bg-[#fff8e1] text-slate-950');

  if (item.href) {
    return (
      <DropdownMenuItem key={item.href} asChild className={className}>
        <a href={item.href} target='_blank' rel='noopener noreferrer'>
          <NavItemContent item={item} isActive={active} />
        </a>
      </DropdownMenuItem>
    );
  }

  if (item.to) {
    return (
      <DropdownMenuItem key={item.to} asChild className={className}>
        <Link to={item.to} aria-current={active ? 'page' : undefined}>
          <NavItemContent item={item} isActive={active} />
        </Link>
      </DropdownMenuItem>
    );
  }

  return (
    <DropdownMenuItem
      key={item.id ?? item.label}
      className={className}
      onSelect={item.onClick}
    >
      <NavItemContent item={item} isActive={active} />
    </DropdownMenuItem>
  );
};

const SectionStack = ({
  sections,
  pathname,
}: {
  sections: Array<NavGroup>;
  pathname: string;
}) => (
  <>
    {sections.map((section, index) => (
      <div key={section.id}>
        {index > 0 ? (
          <DropdownMenuSeparator className='-mx-0.5 my-1.5 bg-slate-200' />
        ) : null}
        <DropdownMenuGroup>
          {section.label ? (
            <DropdownMenuLabel className='px-2 pb-1 pt-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400'>
              {section.label}
            </DropdownMenuLabel>
          ) : null}
          <div className='flex flex-col gap-0.5'>
            {section.items.map((item) => (
              <NavDropdownItem
                key={item.id ?? item.to ?? item.href ?? item.label}
                item={item}
                pathname={pathname}
              />
            ))}
          </div>
        </DropdownMenuGroup>
      </div>
    ))}
  </>
);

export const NavSection = ({
  label,
  sectionKey,
  sections,
  pathname,
  activeMenu,
  setActiveMenu,
  columns = 1,
  widthClassName = 'w-80',
  align = 'start',
}: NavSectionProps) => {
  const sectionActive =
    typeof isActiveSection === 'function' &&
    isActiveSection(pathname, sectionKey);
  const leftSections = sections.filter((section) => (section.column ?? 1) === 1);
  const rightSections = sections.filter((section) => section.column === 2);

  return (
    <DropdownMenu
      modal={false}
      open={activeMenu === sectionKey}
      onOpenChange={(open) => {
        setActiveMenu(open ? sectionKey : null);
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          variant='ghost'
          onMouseEnter={() => {
            if (activeMenu !== null && activeMenu !== sectionKey) {
              setActiveMenu(sectionKey);
            }
          }}
          className={cn(
            // h-full + after:bottom-0 keeps the active bar flush with the
            // header bottom border (0px gap) without overflowing content (ENR-3910).
            'group relative h-full gap-1 rounded-none px-3 text-[13px] font-medium tracking-[-0.01em]',
            'text-slate-700 transition-colors hover:bg-slate-100/80 hover:text-slate-950',
            'data-[state=open]:bg-slate-100/80 data-[state=open]:text-slate-950',
            sectionActive &&
              'font-semibold text-slate-950 after:absolute after:inset-x-2.5 after:bottom-0 after:h-[3px] after:rounded-full after:bg-[#FFC300]',
          )}
        >
          {label}
          <ChevronDown className='size-3.5 text-slate-400 transition-transform group-data-[state=open]:rotate-180 group-data-[state=open]:text-slate-700' />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align={align}
        sideOffset={10}
        collisionPadding={12}
        className={cn(
          'max-h-[calc(100vh-96px)] max-w-[calc(100vw-2rem)] overflow-y-auto rounded-xl border border-slate-200 bg-white p-1.5 text-slate-950',
          'shadow-lg shadow-black/[0.06]',
          widthClassName,
        )}
      >
        <motion.div
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.1, ease: 'easeOut' }}
        >
          {columns === 2 ? (
            <div className='grid grid-cols-1 md:grid-cols-2'>
              <div className='md:pr-2'>
                <SectionStack sections={leftSections} pathname={pathname} />
              </div>
              <div className='md:border-l md:border-slate-200 md:pl-2'>
                <SectionStack sections={rightSections} pathname={pathname} />
              </div>
            </div>
          ) : (
            <SectionStack sections={sections} pathname={pathname} />
          )}
        </motion.div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
